// Email-blast DTOs mirror the API (PascalCase). State values are the C# enum NAMES (ORB-357).
// A "blast" is the controlled, canary-then-ramp send of one email touchpoint to its target audience.

// Lifecycle of a blast. Drives the State badge and which controls are offered.
export type EmailBlastState =
  | 'Canary'      // initial small sample is sending
  | 'Measuring'   // canary sent; holding to measure bounce/complaint rates
  | 'Releasing'   // gates passed; ramping the remaining audience in stages
  | 'Paused'      // breaker tripped (or manually paused) — resumable
  | 'Complete'    // whole audience sent
  | 'Failed'      // unrecoverable error
  | 'Cancelled'   // operator-cancelled

// Terminal states accept no further actions (Cancel is hidden, Resume never applies).
export const TERMINAL_BLAST_STATES: EmailBlastState[] = ['Complete', 'Failed', 'Cancelled']

export function isTerminalBlastState(state: EmailBlastState): boolean {
  return TERMINAL_BLAST_STATES.includes(state)
}

// One transition recorded on the blast.
export interface EmailBlastStateHistoryEntryDto {
  State: EmailBlastState
  Timestamp: string // ISO 8601
  Details?: string | null
}

// A circuit-breaker evaluation at a sampling checkpoint. `Decision` is the breaker's call;
// when it Pauses, `Reason` is the tripped condition (e.g. "Hard bounce 6.2% over 5% limit").
export interface EmailBlastGateDecisionDto {
  Timestamp: string // ISO 8601
  Decision: string  // breaker outcome (e.g. "Continue" | "Pause")
  HardBouncePercent: number
  ComplaintPercent: number
  SampleSize: number
  Reason: string
}

// Running send counters + derived rates the breaker watches.
export interface EmailBlastStatsDto {
  Queued: number
  Sent: number
  Delivered: number
  Bounced: number
  Complained: number
  Skipped: number
  Failed: number
  HardBouncePercent: number
  ComplaintPercent: number
}

/** Rows that have ever left the queue (dispatched). The pre-send guard keys "has it started?" on this
 *  being zero, and the lock banner uses it for send progress. Skipped (suppressed) rows never dispatch. */
export function dispatchedCount(stats: EmailBlastStatsDto): number {
  return stats.Sent + stats.Delivered + stats.Bounced + stats.Complained + stats.Failed
}

// Full view of a blast: config snapshot (the gate thresholds + ramp plan captured at launch),
// live stats, the state/gate history, and lifecycle timing.
export interface EmailBlastViewDto {
  Id: string
  WorkspaceId: string
  ClientCompanyId: string
  CampaignId: string
  WebId: string
  TouchPointId: string
  TargetFileImportIds: string[]
  TotalAudience: number
  ExcludedCount: number
  /** Recipients pulled after staging because they registered during the window (ORB-409). Live
   *  recipients = TotalAudience − this. 0 unless conversion-cancels landed on this blast. */
  PulledAfterRegistrationCount: number
  SendAtAbsoluteUtc: string // ISO 8601 — the blast's scheduled send anchor
  State: EmailBlastState
  StateHistory: EmailBlastStateHistoryEntryDto[]

  // Config snapshot — the breaker/ramp settings this blast launched with.
  CanarySize: number
  MeasurementWindowMinutes: number
  EmailsPerMinute: number
  MaxHardBouncePercent: number
  MaxComplaintPercent: number
  MinCanarySendsForRelease: number
  RampStagePercents: number[]
  CurrentRampStage: number

  // A/B subject split test (ORB-453). IsSplitTest is false for a normal blast.
  IsSplitTest: boolean
  SubjectVariantKeys: string[]
  WinMetric: 'Opens' | 'Clicks' | null
  WinningVariantKey: string | null
  SplitTestResults: EmailBlastVariantResultDto[]

  // Set when a split test was configured but this blast couldn't run one and sent the control subject to
  // everyone (ORB-472). IsSplitTest false + this null = no test was ever configured; IsSplitTest false +
  // a record = one was, and was skipped. Absent on blasts created before the field existed.
  SplitTestSkip?: EmailBlastSplitTestSkipDto | null

  Stats: EmailBlastStatsDto
  GateDecisions: EmailBlastGateDecisionDto[]

  // Engagement aggregate (ORB-384). Null on write/monitor paths that don't request it. On the blast
  // LIST only counts + rates are filled; on the blast DETAIL the top links + approx unsubscribe are too.
  Engagement?: BlastEngagementDto | null

  // Lifecycle timing (all ISO 8601; null until the milestone is reached).
  CanaryReleasedAtUtc?: string | null
  MeasurementEndsAtUtc?: string | null
  DrainStartedAtUtc?: string | null
  CompletedAtUtc?: string | null
  ResumedAtUtc?: string | null
  CreatedDate: string
}

// Per-variant measured engagement in an A/B split test's cohort at the winner decision (ORB-453).
export interface EmailBlastVariantResultDto {
  VariantKey: string
  Delivered: number
  Opened: number
  Clicked: number
}

// Why a configured A/B split test didn't run (ORB-472). The counts are the ones the skip was decided on,
// so the monitor can explain the reason rather than just name it.
export type SplitTestSkipReason = 'BelowPerVariantFloor' | 'NoChallengerSubject' | 'NoRemainder' | 'RecipientLocal'

export interface EmailBlastSplitTestSkipDto {
  Timestamp: string
  Reason: SplitTestSkipReason
  /** Eligible recipients at blast creation, after the eligibility filters. */
  EligibleCount: number
  /** The cohort the configured percent resolved to; 0 for non-count reasons. */
  CohortSize: number
  /** The per-variant recipient floor in force for this environment at create. */
  RequiredPerVariant: number
  /** Subject variants the test would have run (control + challengers). */
  VariantCount: number
}

// ── Engagement analytics (ORB-384) ──────────────────────────────────────────
// Rates are whole/one-decimal percents and are NULL when there's no denominator (delivered === 0) —
// the UI renders that as "—", never a misleading 0%. A measured zero (delivered > 0) is a real 0.
// Clicks are the trustworthy signal; open rate is inflated by inbox privacy (Apple Mail Privacy).

export interface ClickedLinkDto {
  Url: string
  Count: number
}

// Engagement for one blast. List = counts + rates; detail = + TopClickedLinks + the unsubscribe estimate.
export interface BlastEngagementDto {
  Delivered: number
  OpenedDistinct: number
  ClickedDistinct: number
  Bounced: number
  Complained: number
  OpenRatePercent: number | null
  ClickRatePercent: number | null
  ClickToOpenRatePercent: number | null
  BounceRatePercent: number | null
  TopClickedLinks: ClickedLinkDto[]
  // Approximate recipients who unsubscribed within UnsubscribeWindowDays of this send (detail only).
  ApproxUnsubscribesInWindow: number | null
  UnsubscribeWindowDays: number
}

// One recipient in a blast's drill-down. ContactId / LeadId / FileImportRowId pick the ORB-383
// contact-route anchor (lead preferred, else audience). Opens/clicks read "—" when never delivered.
export interface BlastRecipientDto {
  Id: string
  RecipientEmail: string
  RecipientName?: string | null
  ContactId?: string | null
  LeadId?: string | null
  FileImportRowId?: string | null
  Status: string
  OpenCount: number
  ClickCount: number
  FirstOpenedAtUtc?: string | null
  FirstClickedAtUtc?: string | null
  LastClickedUrl?: string | null
  SentAtUtc?: string | null
  DeliveredAtUtc?: string | null
  BouncedAtUtc?: string | null
}

export interface BlastRecipientsPageDto {
  Items: BlastRecipientDto[]
  Total: number
  Skip: number
  Take: number
}

// One day of the campaign engagement trend. Date is UTC midnight (ISO 8601).
export interface EmailEngagementTrendPointDto {
  Date: string
  Sends: number
  Opens: number
  Clicks: number
}

// One blast's line in the campaign rollup table. Rates null when the blast has delivered nothing yet.
export interface CampaignBlastSummaryDto {
  BlastId: string
  WebId: string
  State: EmailBlastState
  SendAtAbsoluteUtc: string
  Sent: number
  Delivered: number
  ClickRatePercent: number | null
  ClickToOpenRatePercent: number | null
  OpenRatePercent: number | null
}

// Campaign-grain rollup across the campaign's marketing blasts. Counts are send-grain; unsubscribe is
// distinct-recipient. Rates are over delivered (null when nothing delivered).
export interface CampaignEmailRollupDto {
  CampaignId: string
  BlastCount: number
  Sent: number
  Delivered: number
  Opened: number
  Clicked: number
  Bounced: number
  Complained: number
  UnsubscribedCount: number
  OpenRatePercent: number | null
  ClickRatePercent: number | null
  ClickToOpenRatePercent: number | null
  UnsubscribeRatePercent: number | null
  Trend: EmailEngagementTrendPointDto[]
  Blasts: CampaignBlastSummaryDto[]
}

// State pill colors — keyed by the C# enum NAME. In-progress states read neutral/active,
// Paused reads as a warning, Failed/Cancelled as danger, Complete as success.
// (Mirrors the DOMAIN_STATUS_COLORS idiom in sendingDomains.ts rather than reusing it.)
export const BLAST_STATE_COLORS: Record<EmailBlastState, string> = {
  Canary: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  Measuring: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
  Releasing: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
  Paused: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  Complete: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  Failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  Cancelled: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
}

export const BLAST_STATE_LABELS: Record<EmailBlastState, string> = {
  Canary: 'Canary',
  Measuring: 'Measuring',
  Releasing: 'Releasing',
  Paused: 'Paused',
  Complete: 'Complete',
  Failed: 'Failed',
  Cancelled: 'Cancelled',
}

// Short, human explanation of each state for the detail header.
export const BLAST_STATE_DESCRIPTIONS: Record<EmailBlastState, string> = {
  Canary: 'Sending the initial canary sample.',
  Measuring: 'Canary sent — measuring bounce and complaint rates before releasing the rest.',
  Releasing: 'Gates passed — ramping the remaining audience in stages.',
  Paused: 'Send is paused. Resume to continue, or cancel to stop it.',
  Complete: 'The full audience has been sent.',
  Failed: 'The send failed and cannot continue.',
  Cancelled: 'The send was cancelled.',
}
